import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { usePermissions } from "../../contexts/PermissionsContext";
import {
  fetchOrders,
  fetchOrderById,
  updateOrderStatus,
  searchOrder,
  clearSearchResults,
  fetchRetryPendingOrders,
  retryPendingOrder,
} from "../../RTK/orders/GetAllOrders";
import {
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  Search,
  CalendarClock,
  MessageCircle,
  MessageSquare,
  CheckCheck,
  AlertCircle,
  ArrowRight,
  Code2,
  Globe,
  RefreshCw,
  Bell,
  BellOff,
  Grid3x3,
  ChevronDown,
  Hash, // لـ transaction_id
  Building2, // لـ order_agent_id
  UserCircle2, // لـ client_order_id
  Package,
  Lock,
  Plus,
  BadgePlus,
  LayoutDashboard,
} from "lucide-react";
import { ProtectedComponent } from "../../components/ProtectedComponent/ProtectedComponent";

import { toast } from "react-toastify";
import OrderDetailsPopup from "./order-popups/OrderDetailsPopup";
import SMMOrderDetailsPopup from "./order-popups/SMMOrderDetailsPopup";
import SearchResultsPopup from "./order-popups/SearchResultsPopup";
import { useNotifications } from "../../hooks/useNotifications";
import dayjs from "dayjs";
import CreateOrderPopup from "./order-popups/CreateOrderPopup";

//  دالة لتنسيق الوقت بالشكل المطلوب
const formatTimeAgo = (dateString) => {
  if (!dateString) return "N/A";

  const now = dayjs();
  const date = dayjs(dateString);
  const diffInSeconds = now.diff(date, "second");
  const diffInMinutes = now.diff(date, "minute");
  const diffInHours = now.diff(date, "hour");
  const diffInDays = now.diff(date, "day");
  const diffInMonths = now.diff(date, "month");
  const diffInYears = now.diff(date, "year");

  if (diffInSeconds < 60) {
    return `منذ ${diffInSeconds} ثانية`;
  } else if (diffInMinutes < 60) {
    return `منذ ${diffInMinutes} دقيقة`;
  } else if (diffInHours < 24) {
    return `منذ ${diffInHours} ساعة`;
  } else if (diffInDays < 30) {
    return `منذ ${diffInDays} يوم`;
  } else if (diffInMonths < 12) {
    return `منذ ${diffInMonths} شهر`;
  } else {
    return `منذ ${diffInYears} سنة`;
  }
};

function Order() {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  // ✅ الصلاحيات
  const canViewOrderDetails = hasPermission("view_order_details");
  const canUpdateOrderStatus = hasPermission("update_order_status");
  const canSearchOrders = hasPermission("search_orders");
  const canRetryPendingOrders = hasPermission("retry_pending_orders");
  const canCreateStaffOrder = hasPermission("create_staff_order");
  const {
    orders = [],
    status,
    error,
    currentOrder,
    searchResults,
    retryPendingOrders,
  } = useSelector((state) => state.orders);
  const { dailyCardPendingCount, soundEnabled, toggleSound } =
    useNotifications();
  const ordersList = orders?.results?.orders || [];
  const Count = orders?.results?.count || {};

  const getInitialPageSize = () => {
    const savedPageSize = localStorage.getItem("ordersPageSize");
    if (savedPageSize) {
      const parsed = parseInt(savedPageSize, 10);
      // التحقق من أن القيمة ضمن النطاق المسموح
      if (parsed >= 20 && parsed <= 100) {
        return parsed;
      }
    }
    return 20; // القيمة الافتراضية
  };

  const [statusFilter, setStatusFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("");
  const [agents, setAgents] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(getInitialPageSize);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("transaction_id");
  const [showSearchFieldDropdown, setShowSearchFieldDropdown] = useState(false);
  const [showSearchPopup, setShowSearchPopup] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const totalPages = Math.ceil(orders?.count / pageSize);
  const [isSearchPopupOpen, setIsSearchPopupOpen] = useState(false);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [retryingOrderId, setRetryingOrderId] = useState(null);
  // ✅ إضافة state للتحديث التلقائي للوقت
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [showCreateOrderPopup, setShowCreateOrderPopup] = useState(false);

  const [errorPopup, setErrorPopup] = useState({
    show: false,
    error: null,
    detail: null,
    orderId: null,
  });

  // Check if the current order is an SMM order
  const isSMMOrder =
    currentOrder?.[0]?.order_data?.product_requirements_data &&
    typeof currentOrder[0].order_data.product_requirements_data === "object" &&
    currentOrder[0].order_data.product_requirements_data.phone;

  // Function to get order type from transaction_id
  const getOrderType = (transactionId) => {
    if (!transactionId) return null;

    if (transactionId.startsWith("DC-API-")) {
      return {
        icon: Code2,
        color: "text-purple-400",
        bgColor: "bg-purple-500/10",
        borderColor: "border-purple-500/30",
      };
    } else if (transactionId.startsWith("DC-DASH-")) {
      return {
        icon: LayoutDashboard, 
        color: "text-yellow-400",
        bgColor: "bg-yellow-500/10",
        borderColor: "border-yellow-500/30",
      };
    } else if (transactionId.startsWith("DC-")) {
      return {
        icon: Globe,
        color: "text-blue-400",
        bgColor: "bg-blue-500/10",
        borderColor: "border-blue-500/30",
      };
    }

    return null;
  };

  // ✅ خيارات البحث
  const searchOptions = [
    {
      value: "transaction_id",
      label: "رقم الطلب",
      placeholder: "ابحث برقم الطلب (DC-12345)",
      icon: Hash, // ✅ أيقونة Hash
      iconColor: "text-blue-400",
    },
    {
      value: "order_agent_id",
      label: "معرف الوكيل",
      placeholder: "ابحث بمعرف الوكيل",
      icon: Building2, // ✅ أيقونة Building
      iconColor: "text-purple-400",
    },
    {
      value: "client_order_id",
      label: "معرف العميل",
      placeholder: "ابحث بمعرف العميل",
      icon: UserCircle2, // ✅ أيقونة User
      iconColor: "text-green-400",
    },
    {
      value: "product_requirements_data",
      label: "بيانات المنتج",
      placeholder: "ابحث ببيانات المنتج (رقم حساب، رقم هاتف)",
      icon: Package, // ✅ أيقونة Package
      iconColor: "text-orange-400",
    },
  ];

  const currentOption = searchOptions.find((opt) => opt.value === searchField);

  // ✅ تحديث الوقت كل ثانية
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchData = () => {
      dispatch(
        fetchOrders({
          status: statusFilter,
          agent: agentFilter || "all",
          page,
          page_size: pageSize,
        }),
      ).then((action) => {
        if (action.payload?.results?.agents_name) {
          setAgents(action.payload.results.agents_name);
        }
      });
    };

    fetchData();

    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(fetchData, 3000);
    }

    return () => clearInterval(intervalId);
  }, [dispatch, statusFilter, agentFilter, page, pageSize, autoRefresh]);

  useEffect(() => {
    dispatch(fetchRetryPendingOrders());

    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(() => {
        dispatch(fetchRetryPendingOrders());
      }, 3000);
    }

    return () => clearInterval(intervalId);
  }, [dispatch, autoRefresh]);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1); // إعادة تعيين الصفحة للأولى
    localStorage.setItem("ordersPageSize", newSize.toString()); //  حفظ في localStorage
  };

  const handleAgentChange = (e) => {
    setAgentFilter(e.target.value);
  };

  const handleSearch = () => {
    if (!canSearchOrders) {
      toast.error("ليس لديك صلاحية للبحث في الطلبات 🔒");
      return;
    }

    if (!searchQuery.trim()) {
      toast.error("الرجاء إدخال كلمة بحث");
      return;
    }

    dispatch(clearSearchResults());
    setIsLoadingSearch(true);

    dispatch(
      searchOrder({
        query: searchQuery,
        search_field: searchField,
        page: 1,
      }),
    )
      .unwrap()
      .then(() => {
        setIsSearchPopupOpen(true);
        setIsLoadingSearch(false);
      })
      .catch((error) => {
        console.error("Search error:", error);
        toast.error("فشل البحث عن الطلبات");
        setIsLoadingSearch(false);
      });
  };

  const handleLoadMoreSearch = (page) => {
    if (isLoadingSearch) return;

    setIsLoadingSearch(true);
    dispatch(
      searchOrder({
        query: searchQuery,
        search_field: searchField, // ✅ إضافة نوع البحث
        page,
      }),
    )
      .unwrap()
      .then(() => {
        setIsLoadingSearch(false);
      })
      .catch((error) => {
        console.error("Load more error:", error);
        setIsLoadingSearch(false);
      });
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleCloseSearchPopup = () => {
    setIsSearchPopupOpen(false);
    setSearchQuery("");
    dispatch(clearSearchResults());
  };

  const handleOrderClick = (orderId) => {
    if (!canViewOrderDetails) {
      toast.error("ليس لديك صلاحية لعرض تفاصيل الطلبات 🔒");
      return;
    }

    setIsLoadingDetails(true);
    setSelectedOrder(orderId);

    dispatch(fetchOrderById(orderId))
      .unwrap()
      .then(() => {
        setIsLoadingDetails(false);
      })
      .catch((error) => {
        console.error("Error loading order details:", error);
        setIsLoadingDetails(false);
        setSelectedOrder(null);
      });
  };

  const handleCloseOrderPopup = () => {
    setSelectedOrder(null);
    setIsLoadingDetails(false);
  };

  const handleRetryOrder = async (order_id) => {
    if (!canRetryPendingOrders) {
      toast.error("ليس لديك صلاحية لإعادة توجيه الطلبات 🔒");
      return;
    }

    setRetryingOrderId(order_id);
    try {
      await dispatch(retryPendingOrder(order_id)).unwrap();
      toast.success("تم إعادة توجيه الطلب بنجاح!");
      dispatch(fetchRetryPendingOrders());
    } catch (error) {
      console.error("Retry error:", error);

      setErrorPopup({
        show: true,
        error: error.error || "حدث خطأ غير متوقع",
        detail: error.detail || error.message || "لا توجد تفاصيل إضافية",
        orderId: order_id,
      });
    } finally {
      setRetryingOrderId(null);
    }
  };

  const getReplayStatus = (replay, orderStatus) => {
    if (!replay || replay === null || replay === "-") {
      return {
        icon: MessageSquare,
        color: "text-gray-400",
        bgColor: "bg-gray-500/10",
        text: "لا يوجد رد",
        show: true,
      };
    }

    const successKeywords = [
      "successful",
      "تم",
      "success",
      "completed",
      "نجح",
      "بنجاح",
    ];
    const errorKeywords = ["failed", "error", "فشل", "خطأ", "cancelled"];

    const replayLower = replay.toLowerCase();
    const isSuccess = successKeywords.some((keyword) =>
      replayLower.includes(keyword.toLowerCase()),
    );
    const isError = errorKeywords.some((keyword) =>
      replayLower.includes(keyword.toLowerCase()),
    );

    if (isSuccess || orderStatus === "completed") {
      return {
        icon: CheckCheck,
        color: "text-green-400",
        bgColor: "bg-green-500/10",
        text: replay.length > 50 ? replay.substring(0, 50) + "..." : replay,
        show: true,
      };
    } else if (isError || orderStatus === "cancelled") {
      return {
        icon: AlertCircle,
        color: "text-red-400",
        bgColor: "bg-red-500/10",
        text: replay.length > 50 ? replay.substring(0, 50) + "..." : replay,
        show: true,
      };
    } else {
      return {
        icon: MessageSquare,
        color: "text-blue-400",
        bgColor: "bg-blue-500/10",
        text: replay.length > 50 ? replay.substring(0, 50) + "..." : replay,
        show: true,
      };
    }
  };

  const StatusBadge = ({ status, isProcessing = false, order = null }) => {
    const styles = {
      completed: {
        bg: "bg-green-500/10",
        text: "text-green-500",
        icon: CheckCircle,
        label: "مكتمل",
      },
      pending: {
        bg: "bg-yellow-500/10",
        text: "text-yellow-500",
        icon: Clock,
        label: "قيد الانتظار",
      },
      cancelled: {
        bg: "bg-red-500/10",
        text: "text-red-500",
        icon: XCircle,
        label: "ملغي",
      },
      processing: {
        bg: "bg-blue-500/10",
        text: "text-blue-500",
        icon: Loader2,
        label: "قيد المعالجة",
      },
    };

    const getStyle = (status, isProcessing, order) => {
      if (
        status === "pending" &&
        isProcessing &&
        order &&
        order.cheapest_agent_name === "Daily Card"
      ) {
        return styles.processing;
      }
      return styles[status] || styles.pending;
    };

    const style = getStyle(status, isProcessing, order);
    const Icon = style.icon;

    const showSpinningAnimation =
      status === "pending" &&
      isProcessing &&
      order?.cheapest_agent_name === "Daily Card";

    // ✅ الحصول على "بواسطة" بناءً على الحالة
    const getByInfo = () => {
      if (status === "completed" && order?.approved_by) {
        return order.approved_by;
      }
      if (status === "cancelled" && order?.approved_by) {
        return order.approved_by;
      }
      if (status === "pending" && order?.processed_by) {
        return order.processed_by;
      }
      return null;
    };

    const byInfo = getByInfo();

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
      >
        <Icon
          className={`w-3.5 h-3.5 flex-shrink-0 ${showSpinningAnimation ? "animate-spin" : ""}`}
        />
        <span className="whitespace-nowrap">
          {style.label}
          {byInfo && <span className="opacity-75"> {byInfo}</span>}
        </span>
      </span>
    );
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleUpdateStatus = (order_id, status, replay = null) => {
    dispatch(updateOrderStatus({ order_id, status, replay }));
  };

  const retryOrdersList = retryPendingOrders?.orders || [];
  const filteredOrders = activeTab === "retry" ? retryOrdersList : ordersList;

  return (
    <div className="p-6">
      {/* Statistics Cards */}
      <div className="p-2 sm:p-4 bg-gray-900 rounded-lg shadow-xl mb-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
          {/* ✅ Completed Orders Card */}
          <div className="bg-gray-800 p-2 sm:p-4 rounded-lg border border-green-500/20 hover:bg-green-800/50 transition-all ease-in-out transform">
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3 rtl:space-x-reverse">
              <div className="bg-green-500/20 p-2 sm:p-3 rounded-full shadow-lg">
                <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500" />
              </div>
              <div className="text-center sm:text-right w-full">
                <p className="text-xs sm:text-sm font-medium text-gray-200">
                  الطلبات المكتملة
                </p>
                <h3 className="text-xl sm:text-3xl font-bold text-white mt-0.5 sm:mt-1">
                  {Count.count_completed}
                </h3>
              </div>
            </div>
          </div>

          {/* ✅ Pending Orders Card */}
          <div className="bg-gray-800 p-2 sm:p-4 rounded-lg border border-yellow-500/20 hover:bg-yellow-800/50 transition-all ease-in-out transform">
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3 rtl:space-x-reverse">
              <div className="bg-yellow-500/20 p-2 sm:p-3 rounded-full shadow-lg">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500" />
              </div>
              <div className="text-center sm:text-right w-full">
                <p className="text-xs sm:text-sm font-medium text-gray-200">
                  الطلبات المعلقة
                </p>
                <h3 className="text-xl sm:text-3xl font-bold text-white mt-0.5 sm:mt-1">
                  {Count.pending_pending}
                </h3>
              </div>
            </div>
          </div>

          {/* ✅ Cancelled Orders Card */}
          <div className="bg-gray-800 p-2 sm:p-4 rounded-lg border border-red-500/20 hover:bg-red-800/50 transition-all ease-in-out transform">
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3 rtl:space-x-reverse">
              <div className="bg-red-500/20 p-2 sm:p-3 rounded-full shadow-lg">
                <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />
              </div>
              <div className="text-center sm:text-right w-full">
                <p className="text-xs sm:text-sm font-medium text-gray-200">
                  الطلبات الملغية
                </p>
                <h3 className="text-xl sm:text-3xl font-bold text-white mt-0.5 sm:mt-1">
                  {Count.count_cancelled}
                </h3>
              </div>
            </div>
          </div>

          {/* ✅ Daily Card Pending Orders Card with Sound Notification */}
          <div className="bg-gray-800 p-2 sm:p-4 rounded-lg border border-blue-500/20 hover:bg-blue-800/50 transition-all ease-in-out transform relative">
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3 rtl:space-x-reverse">
              <div className="bg-blue-500/20 p-2 sm:p-3 rounded-full shadow-lg">
                <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 animate-spin" />
              </div>
              <div className="flex-1 text-center sm:text-right w-full">
                <p className="text-xs sm:text-sm font-medium text-gray-200">
                  المعلقة Daily Card
                </p>
                <h3 className="text-xl sm:text-3xl font-bold text-white mt-0.5 sm:mt-1">
                  {dailyCardPendingCount}
                </h3>
              </div>
              {/* ✅ Sound Toggle Button */}
              <button
                onClick={toggleSound}
                className={`absolute top-1 left-1 sm:relative sm:top-auto sm:left-auto p-1.5 sm:p-2 rounded-lg transition-all ${
                  soundEnabled
                    ? "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                    : "bg-slate-700/50 text-slate-400 hover:bg-slate-700"
                }`}
                title={
                  soundEnabled
                    ? "إيقاف الإشعارات الصوتية"
                    : "تفعيل الإشعارات الصوتية"
                }
              >
                {soundEnabled ? (
                  <Bell className="h-3.5 w-3.5 sm:h-5 sm:w-5 animate-pulse" />
                ) : (
                  <BellOff className="h-3.5 w-3.5 sm:h-5 sm:w-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-6">
        {/* All Orders Tab */}
        <button
          onClick={() => setActiveTab("all")}
          className={`flex-1 py-2.5 sm:py-3 px-4 sm:px-6 rounded-xl font-medium transition-all ${
            activeTab === "all"
              ? "bg-yellow-500 text-black shadow-lg scale-[1.02]"
              : "bg-slate-800 text-slate-300 hover:bg-slate-700"
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Clock className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            <span className="text-sm sm:text-base whitespace-nowrap">
              جميع الطلبات
            </span>
          </div>
        </button>

        {/* ✅ Retry Orders Tab - مرئي للجميع */}
        <button
          onClick={() => setActiveTab("retry")}
          className={`flex-1 py-2.5 sm:py-3 px-4 sm:px-6 rounded-xl font-medium transition-all relative ${
            activeTab === "retry"
              ? "bg-yellow-500 text-black shadow-lg scale-[1.02]"
              : "bg-slate-800 text-slate-300 hover:bg-slate-700"
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <RefreshCw
              className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${autoRefresh && activeTab === "retry" ? "animate-spin" : ""}`}
            />
            <span className="text-sm sm:text-base hidden sm:inline">
              طلبات تحتاج إعادة التوجيه
            </span>
            <span className="text-sm sm:text-base inline sm:hidden">
              إعادة التوجيه
            </span>
            <span
              className={`text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-bold min-w-[20px] sm:min-w-[24px] text-center ${
                (retryPendingOrders?.count ?? 0) > 0
                  ? "bg-red-500 animate-pulse"
                  : "bg-gray-600"
              }`}
            >
              {retryPendingOrders?.count ?? 0}
            </span>
          </div>
        </button>

        {canCreateStaffOrder ? (
          <button
            onClick={() => setShowCreateOrderPopup(true)}
            className="flex-1 py-2.5 sm:py-3 px-4 sm:px-6 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-lg bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-black"
          >
            <BadgePlus className="w-4 h-4 text-black" />
            إنشاء طلب
          </button>
        ) : (
          <div
            className="flex-1 py-2.5 sm:py-3 px-4 sm:px-6 rounded-xl bg-slate-800/50 border border-emerald-500/30 flex items-center justify-center gap-2 cursor-not-allowed"
            title="ليس لديك صلاحية إنشاء طلب"
          >
            <Lock className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-300 text-sm font-medium">
              ليس لديك صلاحية
            </span>
          </div>
        )}
      </div>

      {/* Filters and Search - Only show for "all" tab */}
      {activeTab === "all" && (
        <div className="space-y-3 mb-6">
          {/* ✅ Search Bar - محمي بصلاحية */}
          <ProtectedComponent
            permission="search_orders"
            fallback={
              <div className="flex items-center gap-2 w-full p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                <Lock className="w-5 h-5 text-slate-500" />
                <span className="text-slate-400 text-sm">
                  ليس لديك صلاحية للبحث في الطلبات
                </span>
              </div>
            }
          >
            <div className="flex items-center gap-2 w-full">
              {/* Dropdown لاختيار نوع البحث */}
              <div className="relative">
                <button
                  onClick={() =>
                    setShowSearchFieldDropdown(!showSearchFieldDropdown)
                  }
                  className="flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-800 text-white rounded-xl border border-slate-700 hover:border-slate-600 transition-colors min-w-[140px] sm:min-w-[180px] whitespace-nowrap"
                >
                  <currentOption.icon
                    className={`w-4 h-4 sm:w-5 sm:h-5 ${currentOption.iconColor} flex-shrink-0`}
                  />
                  <span className="text-xs sm:text-sm font-medium hidden sm:inline">
                    {currentOption.label}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform flex-shrink-0 ${
                      showSearchFieldDropdown ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {showSearchFieldDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowSearchFieldDropdown(false)}
                    />

                    <div className="absolute top-full left-0 mt-2 w-[280px] sm:w-[320px] bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden">
                      {searchOptions.map((option) => {
                        const IconComponent = option.icon;
                        return (
                          <button
                            key={option.value}
                            onClick={() => {
                              setSearchField(option.value);
                              setShowSearchFieldDropdown(false);
                              setSearchQuery("");
                            }}
                            className={`w-full text-right px-4 py-3 hover:bg-slate-700 transition-colors ${
                              searchField === option.value
                                ? "bg-yellow-500/20 text-yellow-400 border-r-4 border-yellow-500"
                                : "text-white"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <IconComponent
                                className={`w-5 h-5 ${option.iconColor} flex-shrink-0`}
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium">
                                  {option.label}
                                </div>
                                <div className="text-xs text-slate-400 mt-0.5">
                                  {option.placeholder}
                                </div>
                              </div>
                              {searchField === option.value && (
                                <CheckCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* حقل البحث */}
              <div className="relative flex-1">
                <button
                  onClick={handleSearch}
                  disabled={isLoadingSearch || !searchQuery.trim()}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all z-10 ${
                    isLoadingSearch || !searchQuery.trim()
                      ? "bg-slate-700/50 cursor-not-allowed"
                      : "bg-yellow-500/20 hover:bg-yellow-500/30 cursor-pointer"
                  }`}
                  type="button"
                >
                  {isLoadingSearch ? (
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin text-yellow-400" />
                  ) : (
                    <Search
                      className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors ${
                        !searchQuery.trim()
                          ? "text-slate-500"
                          : "text-yellow-400 hover:text-yellow-300"
                      }`}
                    />
                  )}
                </button>

                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyPress={handleSearchKeyPress}
                  placeholder={currentOption.placeholder}
                  className="bg-slate-900 text-white text-sm sm:text-base border border-slate-700 rounded-xl py-2.5 sm:py-3 pr-12 pl-4 w-full focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all placeholder:text-xs sm:placeholder:text-sm"
                  disabled={isLoadingSearch}
                />
              </div>
            </div>
          </ProtectedComponent>

          {/* ✅ باقي الـ Filters بدون تغيير */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
            {/* Auto Refresh Toggle */}
            <label className="flex items-center gap-2 sm:gap-3 bg-slate-900 border border-slate-700 rounded-xl py-2.5 sm:py-3 px-3 sm:px-4 cursor-pointer hover:bg-slate-800 transition-all">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="w-4 h-4 sm:w-5 sm:h-5 rounded cursor-pointer appearance-none border-2 border-slate-600 bg-slate-900 checked:bg-blue-500 checked:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all"
              />
              <span className="text-slate-300 text-xs sm:text-sm font-medium flex-1 whitespace-nowrap">
                تحديث تلقائي
              </span>
            </label>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-slate-900 border border-slate-700 cursor-pointer rounded-xl py-2.5 sm:py-3 px-3 sm:px-4 text-white text-xs sm:text-sm font-medium focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-all appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23cbd5e1'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "left 0.5rem center",
                backgroundSize: "1.25rem",
                paddingLeft: "2rem",
              }}
            >
              <option value="all">جميع الحالات</option>
              <option value="completed">مكتمل</option>
              <option value="pending">قيد الانتظار</option>
              <option value="cancelled">ملغي</option>
            </select>

            {/* Page Size Selector */}
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="bg-slate-900 border border-slate-700 cursor-pointer rounded-xl py-2.5 sm:py-3 px-3 sm:px-4 text-white text-xs sm:text-sm font-medium focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-all appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23cbd5e1'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "left 0.5rem center",
                backgroundSize: "1.25rem",
                paddingLeft: "2rem",
              }}
            >
              <option value={20}>20 طلب</option>
              <option value={40}>40 طلب</option>
              <option value={60}>60 طلب</option>
              <option value={80}>80 طلب</option>
              <option value={100}>100 طلب</option>
            </select>

            {/* Agent Filter */}
            <select
              value={agentFilter}
              onChange={handleAgentChange}
              className="bg-slate-900 border border-slate-700 cursor-pointer rounded-xl py-2.5 sm:py-3 px-3 sm:px-4 text-white text-xs sm:text-sm font-medium focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-all appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23cbd5e1'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "left 0.5rem center",
                backgroundSize: "1.25rem",
                paddingLeft: "2rem",
              }}
            >
              <option value="">جميع الوكلاء</option>
              {agents.map((agent) => (
                <option key={agent} value={agent}>
                  {agent}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* ✅ Orders Grid */}
      {!isSearchPopupOpen && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-5 gap-4 mt-6">
            {filteredOrders.length > 0 ? (
              filteredOrders.map((order) => {
                const orderData =
                  activeTab === "retry" ? order : order.order_data || order;
                const userData =
                  activeTab === "retry"
                    ? { username: order.username }
                    : order.user_data || order.user || {};
                const orderId = order.order_id || order.id;

                const replayStatus = getReplayStatus(
                  orderData.replay || orderData.reply,
                  orderData.status,
                );
                const orderType = getOrderType(orderData.transaction_id);

                return (
                  <div
                    key={orderId}
                    className={`relative bg-slate-800/50 border rounded-xl p-4 cursor-pointer hover:bg-slate-700/30 transition
                ${
                  activeTab === "retry"
                    ? "border-orange-500 ring-1 ring-orange-500/50 shadow-lg shadow-orange-500/20"
                    : activeTab === "all" &&
                        orderData.cheapest_agent_name === "Daily Card"
                      ? orderData.status === "completed"
                        ? "border-green-500 ring-1"
                        : orderData.status === "cancelled"
                          ? "border-red-500 ring-1"
                          : orderData.status === "pending"
                            ? orderData.is_processing
                              ? "border-blue-500 ring-1"
                              : "border-yellow-500 ring-1"
                            : "border-slate-700 ring-1"
                      : activeTab === "all" && orderData.status === "completed"
                        ? "border-green-500"
                        : activeTab === "all" &&
                            orderData.status === "cancelled"
                          ? "border-red-500"
                          : activeTab === "all" &&
                              orderData.status === "pending"
                            ? "border-yellow-500"
                            : "border-slate-700"
                }
              `}
                    onClick={() => {
                      if (canViewOrderDetails) {
                        handleOrderClick(orderId);
                      } else {
                        toast.error("ليس لديك صلاحية لعرض تفاصيل الطلبات 🔒");
                      }
                    }}
                  >
                    {!canViewOrderDetails && (
                      <div className="absolute top-2 left-2 bg-slate-700/90 p-1.5 rounded-lg z-10">
                        <Lock className="w-3 h-3 text-slate-400" />
                      </div>
                    )}

                    {activeTab === "retry" && (
                      <div className="absolute -top-2 left-0 right-0 z-10 flex justify-center">
                        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg flex items-center gap-1.5">
                          <AlertCircle className="w-3 h-3 animate-pulse" />
                          يحتاج إعادة توجيه فوري
                        </div>
                      </div>
                    )}

                    <div
                      className={`absolute ${activeTab === "retry" ? "top-6" : "-top-3"} left-1/2 transform -translate-x-1/2 z-2 max-w-[70%]`}
                    >
                      <div
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${replayStatus.bgColor} backdrop-blur-sm border border-slate-600/50 shadow-lg`}
                      >
                        <replayStatus.icon
                          className={`w-3 h-3 ${replayStatus.color} flex-shrink-0`}
                        />
                        <span
                          className={`text-xs ${replayStatus.color} font-medium whitespace-nowrap overflow-hidden text-ellipsis`}
                          style={{ maxWidth: "calc(70vw - 100px)" }}
                          title={
                            orderData.replay || orderData.reply || "لا يوجد رد"
                          }
                        >
                          {replayStatus.text}
                        </span>
                      </div>
                    </div>

                    <div className={activeTab === "retry" ? "mt-8" : "mt-2"}>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">
                            {userData.username || "Anonymous"}@
                          </span>
                          {orderData.is_smm_order && (
                            <MessageCircle
                              className="w-4 h-4 text-blue-400"
                              title="طلب SMS"
                            />
                          )}
                        </div>
                        <StatusBadge
                          status={orderData.status}
                          isProcessing={orderData.is_processing}
                          order={orderData}
                        />
                      </div>

                      <div className="flex justify-between items-center mt-3">
                        <span className="text-xs text-slate-400">
                          {orderData.product_name ||
                            orderData.product?.name ||
                            "N/A"}
                        </span>
                        <span
                          className={`text-xs ${
                            orderData.cheapest_agent_name === "Daily Card"
                              ? "text-blue-500 font-extrabold"
                              : "text-slate-400"
                          }`}
                        >
                          {orderData.cheapest_agent_name || "N/A"}
                        </span>
                      </div>

                      <div className="flex justify-between items-center mt-2">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-green-400">
                            $
                            {parseFloat(orderData.total_price || "0").toFixed(
                              2,
                            )}
                          </span>
                          <ArrowRight className="w-3 h-3 text-slate-500" />
                          <span className="text-blue-400">
                            {orderData.quantity?.toLocaleString() || "0"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                          {activeTab === "retry" && order.age_minutes ? (
                            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-orange-500/20 border border-orange-500/30">
                              <CalendarClock className="w-3 h-3 text-orange-400" />
                              <span className="text-orange-400 font-medium">
                                منذ {Math.floor(order.age_minutes)} دقيقة
                              </span>
                            </div>
                          ) : (
                            <span key={currentTime}>
                              {" "}
                              {/* ✅ إضافة key لإجبار re-render */}
                              {formatTimeAgo(orderData.execution_start_time)}
                            </span>
                          )}
                          {orderType && (
                            <div
                              className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${orderType.bgColor} ${orderType.borderColor} border`}
                            >
                              <orderType.icon
                                className={`w-2.5 h-2.5 ${orderType.color}`}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {activeTab === "retry" && (
                        <>
                          {canRetryPendingOrders ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRetryOrder(orderId);
                              }}
                              disabled={retryingOrderId === orderId}
                              className={`w-full mt-4 py-2.5 px-4 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-lg ${
                                retryingOrderId === orderId
                                  ? "bg-orange-600 cursor-not-allowed opacity-70"
                                  : "bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black shadow-orange-500/30 hover:shadow-orange-500/50"
                              }`}
                            >
                              {retryingOrderId === orderId ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  جاري إعادة التوجيه...
                                </>
                              ) : (
                                <>
                                  <RefreshCw className="w-4 h-4" />
                                  إعادة توجيه الطلب
                                </>
                              )}
                            </button>
                          ) : (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              className="w-full mt-4 py-2.5 px-4 rounded-lg bg-slate-800/50 border border-orange-500/30 flex items-center justify-center gap-2"
                            >
                              <Lock className="w-4 h-4 text-orange-400" />
                              <span className="text-orange-300 text-sm font-medium">
                                ليس لديك صلاحية
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div></div>
            )}
          </div>

          {/* No Results Message */}
          {filteredOrders.length === 0 && (
            <div className="flex items-center mx-auto justify-center text-center py-12 text-slate-400 w-full lg:w-1/3 2xl:w-1/3 bg-gray-950 rounded-2xl">
              {activeTab === "retry"
                ? "لا توجد طلبات تحتاج إعادة توجيه"
                : "لم يتم العثور على نتائج"}
            </div>
          )}

          {/* Pagination */}
          {activeTab === "all" && (
            <div className="flex justify-between items-center mt-8 mx-auto bg-gray-800 rounded-xl w-full lg:w-1/3 2xl:w-1/3">
              <button
                className={`px-4 py-2 rounded-xl transition ${
                  page <= 1
                    ? "bg-slate-500 text-slate-200 cursor-not-allowed"
                    : "bg-slate-900 hover:bg-gray-950 text-white"
                }`}
                disabled={page <= 1}
                onClick={() => handlePageChange(page - 1)}
              >
                السابق
              </button>
              <span className="text-slate-400">
                الصفحة {page} من {totalPages}
              </span>
              <button
                className={`px-4 py-2 rounded-xl transition ${
                  page >= totalPages
                    ? "bg-slate-500 text-slate-200 cursor-not-allowed"
                    : "bg-slate-900 hover:bg-gray-950 text-white"
                }`}
                disabled={page >= totalPages}
                onClick={() => handlePageChange(page + 1)}
              >
                التالي
              </button>
            </div>
          )}
        </>
      )}

      {/* Order Details Popup */}
      {selectedOrder && !isLoadingDetails && currentOrder && (
        <>
          {isSMMOrder ? (
            <SMMOrderDetailsPopup
              order={currentOrder}
              StatusBadge={({ status }) => (
                <StatusBadge
                  status={status}
                  isProcessing={currentOrder?.[0]?.order_data?.is_processing}
                  order={currentOrder?.[0]?.order_data}
                />
              )}
              onUpdateStatus={handleUpdateStatus}
              onClose={handleCloseOrderPopup}
              canUpdateStatus={canUpdateOrderStatus} // ✅ تمرير الصلاحية
            />
          ) : (
            <OrderDetailsPopup
              order={currentOrder}
              StatusBadge={({ status }) => (
                <StatusBadge
                  status={status}
                  isProcessing={currentOrder?.[0]?.order_data?.is_processing}
                  order={currentOrder?.[0]?.order_data}
                />
              )}
              onUpdateStatus={handleUpdateStatus}
              onClose={handleCloseOrderPopup}
              canUpdateStatus={canUpdateOrderStatus} // ✅ تمرير الصلاحية
            />
          )}
        </>
      )}

      {/* Loading Details Overlay */}
      {isLoadingDetails && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
            <span className="text-white">جاري تحميل تفاصيل الطلب...</span>
          </div>
        </div>
      )}

      {/* ✅ Search Results Popup - يظهر فقط لما يكون مفتوح */}
      {isSearchPopupOpen && (
        <SearchResultsPopup
          results={searchResults}
          onClose={handleCloseSearchPopup}
          onOrderClick={handleOrderClick}
          StatusBadge={StatusBadge}
          onUpdateStatus={handleUpdateStatus}
          onLoadMore={handleLoadMoreSearch}
          isLoadingMore={isLoadingSearch}
          canViewOrderDetails={canViewOrderDetails} // ✅ تمرير الصلاحية
          canUpdateOrderStatus={canUpdateOrderStatus} // ✅ تمرير الصلاحية
        />
      )}

      {showCreateOrderPopup && (
        <CreateOrderPopup
          onClose={() => setShowCreateOrderPopup(false)}
          onCreated={() => {
            setShowCreateOrderPopup(false);
            dispatch(
              fetchOrders({
                status: statusFilter,
                agent: agentFilter || "all",
                page,
                page_size: pageSize,
              }),
            );
          }}
        />
      )}

      {/* Error Popup */}
      {errorPopup.show && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border-2 border-red-500 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-full">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    فشل إعادة التوجيه
                  </h3>
                  <p className="text-sm text-red-100">
                    Order ID: {errorPopup.orderId}
                  </p>
                </div>
              </div>
              <button
                onClick={() =>
                  setErrorPopup({
                    show: false,
                    error: null,
                    detail: null,
                    orderId: null,
                  })
                }
                className="text-white hover:bg-white/20 p-2 rounded-lg transition"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Error Message */}
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-bold text-red-400 mb-2">
                      رسالة الخطأ:
                    </h4>
                    <p className="text-white text-sm leading-relaxed">
                      {errorPopup.error}
                    </p>
                  </div>
                </div>
              </div>

              {/* Error Details */}
              {errorPopup.detail && (
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <MessageSquare className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-bold text-slate-300 mb-2">
                        تفاصيل الخطأ:
                      </h4>
                      <div className="bg-slate-900/50 rounded-lg p-3 font-mono text-xs text-slate-300 break-all">
                        {errorPopup.detail}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">
                  {errorPopup.orderId}
                </span>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-800 px-6 py-4 flex items-center justify-between border-t border-slate-700">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(
                    `Error: ${errorPopup.error}\nDetail: ${errorPopup.detail}\nOrder ID: ${errorPopup.orderId}`,
                  );
                  toast.success("تم نسخ تفاصيل الخطأ!");
                }}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition"
              >
                <MessageSquare className="w-4 h-4" />
                نسخ التفاصيل
              </button>
              <button
                onClick={() =>
                  setErrorPopup({
                    show: false,
                    error: null,
                    detail: null,
                    orderId: null,
                  })
                }
                className="flex items-center gap-2 px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition font-bold"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Order;